import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { AgentPlanRedirect } from "@/components/shared/AgentPlanRedirect";
import { Outlet, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { SidebarProvider, Sidebar, SidebarInset } from "@/components/ui/sidebar";
import { Sidebar as LayoutSidebar } from "@/components/layout/Sidebar";
import { usePropertySafe } from "@/lib/contexts/PropertyContext";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useURLState } from "@/hooks/useURLState";
import { DashboardWidgetProvider } from "@/lib/contexts/DashboardWidgetContext";
import { AISidebarProvider } from "@/lib/contexts/AISidebarContext";
import { AIPageContextProvider } from "@/lib/contexts/AIPageContext";
import { getAggregatedTrafficMetrics, getAggregatedEventMetrics } from "@/lib/api/bridge";
import { getFilterOptions } from "@/lib/api/external-api";
import { transformToLegacy } from "@/lib/api/bridge/transformers";
import { getRevenueConfig, getPropertyRevenue } from "@/lib/api/api";
import { getWorkspaceRevenueConfig } from "@/lib/api/workspace-team";
import { parseSlugId } from "@/lib/routing/slug-id";
import { interceptDemoAPI, demoGenerators } from "@/lib/utils/demoData";
import FilterBar, { ActiveFilterChips } from "./FilterBar";
import { ShellHeader, ShellHeaderProvider } from "@/components/layout/ShellHeader";
import { isAnalyticsTabPath } from "@/components/layout/shellControl";
import { AnalyticsTabBar, AnalyticsTabsInShell } from "./AnalyticsTabBar";
import { ShellContentProvider } from "@/components/shared/layout/PageShell";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingState, InlineNotice } from "@/components/shared/layout";
import PaywallModal from "@/components/shared/PaywallModal";
import { AppPaywallController } from "@/components/shared/AppPaywallController";
import { TrialBanner } from "@/components/shared/TrialBanner";
import { UpgradeModal } from "@/components/shared/UpgradeModal";
import { useBilling, propertyHasActiveSubscription } from "@/hooks/useBilling";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { useSiteSafe } from "@/lib/contexts/SiteContext";
import { EditorModeProvider } from "@/lib/contexts/EditorModeContext";
import { HugeiconsIcon } from "@hugeicons/react"
import { Alert01Icon } from "@hugeicons/core-free-icons"
import { track } from "@/lib/utils/track"
import { consumeOnboardingCheckout } from "@/lib/utils/trialActivation"

/**
 * New DashboardLayout for Phase 4
 *
 * Uses:
 * - PropertyContext for property & datasource management
 * - URLStateContext for filters/period/sources
 * - API Bridge to fetch data with new format
 */

interface DashboardData {
  // Main analytics data
  stats: any;
  formStats: any;
  clickStats: any;

  // Meta information
  propertyId: string;
  currentDomain: string;

  // Metrics
  metrics?: {
    aggregate?: any[];
    trends?: any;
    topMetrics?: any;
    utmResults?: any[];
    clicks?: any[];
    forms?: any;
    [key: string]: any;
  };

  // Available filter values for the period, read by FilterDropdown. Only the
  // dedicated filter-options fetch populates this; the main dashboard payload
  // leaves it undefined.
  filterOptions?: any;
}

export default function DashboardLayout() {
  const { propertySlugId, workspaceId: workspaceSlugId } = useParams<{ 
    propertySlugId?: string
    workspaceId?: string 
  }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Persist last visited route so we can redirect back after Stripe checkout
  const LAST_ROUTE_KEY = "hmbly_last_route";
  useEffect(() => {
    if (!searchParams.get("stripe_session_id")) {
      localStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  // After Stripe redirect, track conversion and send user back to where they were.
  // If this is the post-onboarding Stripe return, forward to the wizard instead.
  const stripeTrackedRef = useRef(false);
  useEffect(() => {
    if (!searchParams.get("stripe_session_id")) return;
    if (!stripeTrackedRef.current) {
      stripeTrackedRef.current = true;
      track('trial_activated', token, { activation_method: 'stripe' });
    }
    // Backstop: if the onboarding flag is still set (user returned via a /p/* link
    // rather than /dashboard), resume the wizard.
    if (consumeOnboardingCheckout()) {
      track("onboarding_resumed_post_checkout", token, {});
      navigate("/onboarding/profile", { replace: true });
      return;
    }
    const saved = localStorage.getItem(LAST_ROUTE_KEY);
    const target = saved && !saved.includes("stripe_session_id") ? saved : null;
    const t = setTimeout(() => {
      navigate(target || "/dashboard", { replace: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [token]);

  // Analytics tab bar is rendered by the shell (flush under the header) on tab routes.
  const shellBasePath = workspaceSlugId
    ? `/workspace/${workspaceSlugId}`
    : propertySlugId
    ? `/p/${propertySlugId}`
    : ''
  const showShellAnalyticsTabs =
    !location.pathname.includes('/experiments/editor') && !!shellBasePath && isAnalyticsTabPath(location.pathname)

  // Show filter bar only on dashboards, experiments, and heatmaps
  const shouldShowFilterBar = (() => {
    const path = location.pathname
    // Dashboard routes
    const isDashboardRoute = path.includes('/traffic') || 
                            path.includes('/pages') || 
                            path.includes('/devices') ||
                            path.includes('/locations') ||
                            path.includes('/channels') ||
                            path.includes('/llm-referrals') ||
                            path.includes('/clicks') || 
                            path.includes('/forms') ||
                            /\/attribution(\/|$)/.test(path) || // Match /attribution followed by / or end of string
                            path.includes('/export')
    // Experiments are not date-ranged (a test runs on its own dates), so the
    // date/filter bar is hidden there; only the visual editor keeps it.
    const isExperimentRoute = path.includes('/experiments/editor')
    // Heatmaps carry their own page/date controls (Simple heatmap redesign),
    // so the shared filter bar would add a second date picker there.
    // Funnel routes
    const isFunnelRoute = path.includes('/funnels') || 
                          path.includes('/funnel-analyzer')
    
    // Exclude settings and other non-dashboard routes
    const isSettingsRoute = path.includes('/settings')
    
    return (isDashboardRoute || isExperimentRoute || isFunnelRoute) && !isSettingsRoute
  })()
  
  // Check if we're in the visual editor mode
  const isEditorMode = location.pathname.includes('/experiments/editor')

  // The API Access page doesn't depend on account-wide dashboard data, so the
  // "Loading dashboard data..." loader is meaningless there and just overlaps
  // the page while it renders its own (key-scoped) loading states.
  const isApiRoute = /\/api(\/|$)/.test(location.pathname)
  // Analytics tabs render their own skeleton in the loaded layout's geometry.
  // The visitor profile reads the live feed, not the period data, so it owns its loading too.
  const pageOwnsLoading = isAnalyticsTabPath(location.pathname) || /\/visitors\/[^/]+\/?$/.test(location.pathname)
  const onboardingProgress = useOnboardingProgress()
  const isOnboardingStatusPending = onboardingProgress.isLoading
  // Only gate the dashboard if the user has no sites yet — tracking verification
  // state is irrelevant here (stats update every ~10min, not in real-time).
  // Don't block data fetching while onboarding status is loading — they're independent.
  const isOnboardingIncomplete = !isOnboardingStatusPending && onboardingProgress.realSiteCount === 0
  const shouldBypassDashboardData = isApiRoute || isOnboardingIncomplete

  // Always call hooks - safe hooks return null/undefined if provider is not available
  const workspaceContext = useWorkspaceSafe()
  const propertyContext = usePropertySafe()
  
  // Determine which context to use based on route
  const isWorkspaceMode = !!workspaceSlugId && !!workspaceContext
  
  const workspace = workspaceContext?.workspace || null
  const workspaceConnectors: any[] = useMemo(() => workspaceContext?.connectors || [], [workspaceContext?.connectors])
  const workspaceLoading = workspaceContext?.isLoading || false
  const hasHumblyticsTracking = workspaceContext?.hasHumblyticsTracking || false
  const workspacePropertyId = workspaceContext?.propertyId
  const isAggregateMode = workspaceContext?.isAggregateMode || false
  const enabledProperties = workspaceContext?.enabledProperties || []
  // Pre-computed by WorkspaceContext: first 5 enabled properties for aggregate API calls.
  // Memoized so the array reference is stable — otherwise the `|| []` fallback makes
  // buildEffectivePropertyIds/fetchFilterOptionsData churn identity every render.
  const aggregatePropertyIds: string[] = useMemo(() => workspaceContext?.aggregatePropertyIds || [], [workspaceContext?.aggregatePropertyIds])
  
  const property = propertyContext?.property || null
  const propertyLoading = propertyContext?.isLoading || false
  const activeDataSources = isWorkspaceMode
    ? (workspaceContext?.activeConnectors || []).map((c: any) => ({
        dataSourceId: c.connectorId,
        type: c.type,
        name: c.name,
        isActive: c.status === 'connected',
        config: c.config || {},
      }))
    : propertyContext?.activeDataSources || []
  const selectedSourceIds = isWorkspaceMode
    ? workspaceContext?.selectedConnectorIds || []
    : propertyContext?.selectedSourceIds || []
  
  const { state } = useURLState();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [filterOptionsData, setFilterOptionsData] = useState<DashboardData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract property ID - use workspace propertyId if available, otherwise from propertySlugId
  const propertyId = workspacePropertyId || (propertySlugId ? parseSlugId(propertySlugId).id : null);
  const isLoadingContext = isWorkspaceMode ? workspaceLoading : propertyLoading;
  const isDemoProperty = propertyId === '00000';

  // Billing/subscription status
  const billingStatus = useBilling();
  
  // Get site context to check property subscription
  const siteContext = useSiteSafe();
  
  const [showDemoPaywall, setShowDemoPaywall] = useState(false);
  
  // Check if demo paywall has been dismissed
  useEffect(() => {
    if (isDemoProperty && propertyId) {
      const dismissed = localStorage.getItem(`demo-paywall-dismissed-${propertyId}`);
      if (!dismissed) {
        // Show paywall after a short delay so user can see the dashboard first
        const timer = setTimeout(() => {
          setShowDemoPaywall(true);
        }, 2000); // 2 seconds delay
        return () => clearTimeout(timer);
      }
    }
  }, [isDemoProperty, propertyId]);


  // Determine if subscription blocker should show
  // This logic mirrors the Svelte app's behavior:
  // - Check if user has ever activated a subscription (subscriptionExists)
  // - For shared sites, check the property owner's subscription status
  // - Show paywall only if BOTH user and property owner don't have active subscriptions
  const currentProperty = siteContext?.sites?.find(site => site.propertyId === propertyId)
  const propertyOwnerHasAccess = propertyHasActiveSubscription(currentProperty)
  const isSiteContextLoading = siteContext?.isLoadingSites ?? true

  // Never show the paywall until billing data has actually loaded.
  // AppPaywallController uses the same guard; this flag is kept for the
  // disabled prop so the controller doesn't flash before context resolves.
  const isDataLoading = billingStatus.isLoading || isSiteContextLoading;

  const {
    openPricingModal,
    isFreePlan,
    hasActiveTrial,
    hasActiveSubscription,
    isLoading: isSubscriptionLoading,
  } = useSubscription()

  // Auto-nudge for free users who have a trial but haven't upgraded.
  // Hard-blocked accounts are handled by AppPaywallController (persistent).
  // This effect covers the "soft" case: account has a free plan but already
  // started a trial or is otherwise not in the hard-block condition.
  useEffect(() => {
    if (isSubscriptionLoading) return
    if (searchParams.get("stripe_session_id")) return
    if (propertyOwnerHasAccess) return
    if (!isFreePlan || hasActiveTrial || hasActiveSubscription || isDemoProperty) return
    // Hard-blocked accounts are already handled by AppPaywallController's
    // persistent gate — skip the soft nudge to avoid competing opens.
    const isHardBlocked =
      !isDataLoading &&
      !isDemoProperty &&
      !isWorkspaceMode &&
      !billingStatus.subscriptionExists &&
      !billingStatus.hasActiveTrial &&
      !billingStatus.hasActiveSubscription &&
      !propertyOwnerHasAccess
    if (isHardBlocked) return
    const t = setTimeout(() => openPricingModal(), 2000)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionLoading, propertyOwnerHasAccess, isFreePlan, hasActiveTrial, hasActiveSubscription, isDemoProperty, searchParams, isDataLoading, isWorkspaceMode])

  // Revenue config - determines if revenue should be shown on dashboard
  const [showRevenue, setShowRevenue] = useState(false);

  useEffect(() => {
    async function fetchRevenueStatus() {
      if (!token) return;

      try {
        let configured = false;
        let enabled = true;

        // Try workspace-level revenue config first
        if (workspace?.workspaceId) {
          const wsConfig = await getWorkspaceRevenueConfig(workspace.workspaceId, token);
          configured = !!(wsConfig?.stripe?.configured || wsConfig?.foxy?.configured);
          enabled = wsConfig?.stripe?.enabled ?? wsConfig?.foxy?.enabled ?? true;

          // Fallback only when workspace response is structurally empty (not when it's explicitly disconnected).
          const fallbackPropertyId = propertyId || enabledProperties[0]?.propertyId;
          const hasWorkspaceRevenueShape = !!(wsConfig && ("stripe" in wsConfig || "foxy" in wsConfig));
          if (!hasWorkspaceRevenueShape && fallbackPropertyId) {
            const propConfig = await getRevenueConfig(fallbackPropertyId, token);
            configured = !!(propConfig?.revenueSetup?.stripe?.configured || propConfig?.revenueSetup?.foxy?.configured);
            enabled = propConfig?.showRevenueAttribution ?? true;
          }
        } else if (propertyId) {
          const propConfig = await getRevenueConfig(propertyId, token);
          configured = !!(propConfig?.revenueSetup?.stripe?.configured || propConfig?.revenueSetup?.foxy?.configured);
          enabled = propConfig?.showRevenueAttribution ?? true;
        }

        setShowRevenue(configured && enabled);
      } catch {
        setShowRevenue(false);
      }
    }

    fetchRevenueStatus();
  }, [token, workspace?.workspaceId, propertyId, enabledProperties]);

  // Get site name for blocker
  const siteName = workspace?.name || property?.name || undefined;

  const buildEffectivePropertyIds = useCallback((): string[] => (
    isAggregateMode
      ? aggregatePropertyIds
      : propertyId ? [propertyId] : []
  ), [isAggregateMode, aggregatePropertyIds, propertyId]);

  // Fetch dashboard data when filters/period/sources change
  const fetchDashboardData = useCallback(async () => {
    if (shouldBypassDashboardData) {
      setDashboardData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Build effective property IDs — always an array, aggregate endpoint handles both cases
    const effectivePropertyIds = buildEffectivePropertyIds();

    if (effectivePropertyIds.length === 0 || !token) return;

    setIsLoading(true);
    setError(null);
    const willFetchRevenue = !isDemoProperty && effectivePropertyIds.length === 1 && /\/traffic(\/|$|\?)/.test(location.pathname);
    console.log('[Revenue] fetchDashboardData start — willFetchRevenue:', willFetchRevenue, 'pathname:', location.pathname);
    if (willFetchRevenue) setRevenueLoading(true);

    try {
      let statsResponse: any;
      let formStats: any;
      let clickStats: any;

      if (isDemoProperty) {
        // Demo property: use generated mock data
        [statsResponse, formStats, clickStats] = await Promise.all([
          interceptDemoAPI(effectivePropertyIds[0], () => Promise.reject(new Error('demo')), demoGenerators.dashboard),
          interceptDemoAPI(effectivePropertyIds[0], () => Promise.reject(new Error('demo')), demoGenerators.forms),
          interceptDemoAPI(effectivePropertyIds[0], () => Promise.reject(new Error('demo')), demoGenerators.clicks),
        ]);
      } else {
        // All real properties — always use aggregate endpoints
        [statsResponse, formStats, clickStats] = await Promise.all([
          getAggregatedTrafficMetrics(effectivePropertyIds, state.period, state.filters, token),
          getAggregatedEventMetrics(effectivePropertyIds, state.period, state.filters, 'forms', token),
          getAggregatedEventMetrics(effectivePropertyIds, state.period, state.filters, 'clicks', token),
        ]);
      }

      const metrics = statsResponse?.metrics || statsResponse || {};

      const selectedPropertyId = workspaceContext?.selectedPropertyId;
      const currentDomain = isAggregateMode
        ? enabledProperties.filter(p => aggregatePropertyIds.includes(p.propertyId)).map(p => p.domain).filter(Boolean).join(', ')
        : workspace
          ? enabledProperties.find(p => p.propertyId === selectedPropertyId)?.domain
            || workspaceConnectors[0]?.domain
            || workspace.name
          : property
            ? (property.dataSources[0]?.config as any)?.domain || property.name || ''
            : '';

      const data: DashboardData = {
        stats: statsResponse || {},
        formStats: formStats || {},
        clickStats: clickStats || {},
        propertyId: effectivePropertyIds[0] || '',
        currentDomain,
        metrics,
        ...metrics,
      };
      setDashboardData(data);

      // Revenue is fetched independently so it never blocks the initial render.
      // Only needed on the traffic overview page; patches in once it resolves.
      const isTrafficPage = /\/traffic(\/|$|\?)/.test(location.pathname);
      const singlePropertyId = effectivePropertyIds.length === 1 ? effectivePropertyIds[0] : null;
      console.log('[Revenue] isTrafficPage:', isTrafficPage, 'pathname:', location.pathname, 'singlePropertyId:', singlePropertyId, 'isDemoProperty:', isDemoProperty);
      if (!isDemoProperty && singlePropertyId && isTrafficPage) {
        const legacyPeriod = transformToLegacy.period(state.period);
        console.log('[Revenue] firing fetch, period:', legacyPeriod);
        getPropertyRevenue(legacyPeriod, singlePropertyId, token)
          .then((rev: any) => {
            console.log('[Revenue] response:', rev);
            const revenueData = rev?.metrics?.trends?.revenue ?? [];
            const revenueAllData = rev?.metrics?.trends?.revenueAll ?? [];
            const revenueAttributionData = rev?.metrics?.trends?.revenueAttribution ?? [];
            console.log('[Revenue] revenueData length:', revenueData.length);
            setDashboardData((prev) => {
              if (!prev) return prev;
              const prevMetrics = prev.metrics ?? {};
              const prevTrends = prevMetrics?.trends ?? {};
              return {
                ...prev,
                metrics: {
                  ...prevMetrics,
                  trends: { ...prevTrends, revenue: revenueData, revenueAll: revenueAllData, revenueAttribution: revenueAttributionData },
                },
              };
            });
          })
          .catch((err: any) => console.warn('[Revenue] fetch failed:', err))
          .finally(() => setRevenueLoading(false));
      }
    } catch (err) {
      console.error("DashboardLayout: Error fetching dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, [buildEffectivePropertyIds, token, workspace, property, workspaceConnectors, state.period, state.filters, isAggregateMode, aggregatePropertyIds, enabledProperties, isDemoProperty, location.pathname, shouldBypassDashboardData]);

  const fetchFilterOptionsData = useCallback(async () => {
    const effectivePropertyIds = buildEffectivePropertyIds();

    if (effectivePropertyIds.length === 0 || !token) {
      setFilterOptionsData(null);
      return;
    }

    if (isDemoProperty) {
      setFilterOptionsData(null);
      return;
    }

    try {
      const filterOptions = await getFilterOptions(state.period, effectivePropertyIds, token);
      setFilterOptionsData({
        stats: {},
        formStats: {},
        clickStats: {},
        propertyId: effectivePropertyIds[0] || '',
        currentDomain: '',
        filterOptions,
      });
    } catch (err) {
      console.warn("DashboardLayout: Failed to load filter options:", err);
      setFilterOptionsData(null);
    }
  }, [buildEffectivePropertyIds, token, state.period, isDemoProperty]);

  // Memoize filters and aggregate IDs as strings to avoid infinite loop
  const filtersKey = JSON.stringify(state.filters);
  const aggregateIdsKey = aggregatePropertyIds.join(',');

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, token, state.period, filtersKey, isAggregateMode, aggregateIdsKey]);

  // Intentionally does NOT depend on state.filters: the dropdown options come from the
  // unfiltered dataset and must not refetch when a filter is applied. fetchFilterOptionsData
  // is now stable (it closes over memoized values), so depending on it is safe.
  useEffect(() => {
    fetchFilterOptionsData();
  }, [fetchFilterOptionsData]);

  // Loading state
  if (isLoadingContext) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // Error state - workspace/property not found
  // Skip this check for demo site
  if (!workspace && !property && !isLoadingContext && !isDemoProperty) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <HugeiconsIcon icon={Alert01Icon} size={48} className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-[22px] font-semibold">
            {workspaceSlugId ? 'Workspace Not Found' : 'Property Not Found'}
          </h2>
          <p className="text-muted-foreground">
            {workspaceSlugId
              ? "The workspace you're looking for doesn't exist or you don't have access to it."
              : "The property you're looking for doesn't exist or you don't have access to it."}
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-primary hover:underline"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <EditorModeProvider>
      <AgentPlanRedirect />
      <DashboardWidgetProvider propertyId={propertyId || undefined}>
        <AISidebarProvider defaultOpen={false}>
          <AIPageContextProvider dashboardData={dashboardData}>
          <SidebarProvider defaultOpen={!isSidebarCollapsed}>
            <ShellHeaderProvider>
            <LayoutSidebar 
              isCollapsed={isSidebarCollapsed} 
              setIsCollapsed={setIsSidebarCollapsed}
              workspaceSlugId={workspaceSlugId}
              propertySlugId={propertySlugId}
            />
            
            <SidebarInset className="flex flex-col min-h-0 min-w-0 h-full overflow-hidden transition-all duration-300">
              {/* Shell header (White signal v2): 52px bar with the "Group › Page"
                  breadcrumb; dashboards, experiments and heatmaps add the filter
                  controls on the right, with active filter chips underneath. */}
              {!isEditorMode && (
                <ShellHeader
                  below={
                    shouldShowFilterBar && !shouldBypassDashboardData ? (
                      <ActiveFilterChips className="px-5 pb-3" />
                    ) : undefined
                  }
                >
                  {shouldShowFilterBar && !shouldBypassDashboardData && (
                    <FilterBar
                      className="min-w-0 flex-1"
                      dashboardData={dashboardData}
                      filterOptionsData={filterOptionsData}
                      showChips={false}
                    />
                  )}
                </ShellHeader>
              )}
              {/* Analytics tabs sit flush under the header bar (Paper "App v3 · Analytics Overview"). */}
              {showShellAnalyticsTabs && (
                <AnalyticsTabBar placement="shell" basePath={shellBasePath} />
              )}
              {/* Subscription status banners — hidden in editor mode */}
              {!isEditorMode && (
                <>
                  {/* All monetization strips (read-only, payment failed, trial, quota) */}
                  {!propertyOwnerHasAccess && <TrialBanner />}

                </>
              )}

              {/* Main Content Area - Scrollable, full width */}
              {/* In editor mode, render content without padding/max-width for full iframe view */}
{isEditorMode ? (
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                  <Outlet
                    context={{
                      dashboardData: dashboardData
                        ? {
                            ...dashboardData,
                            metrics: dashboardData.metrics || dashboardData.stats?.metrics || {},
                          }
                        : null,
                      filterOptionsData: filterOptionsData
                        ? {
                            ...filterOptionsData,
                            metrics: filterOptionsData.metrics || filterOptionsData.stats?.metrics || {},
                          }
                        : null,
                      isLoading: isLoading || (!shouldBypassDashboardData && !dashboardData),
                      period: state.period,
                      filters: state.filters,
                      sources: selectedSourceIds,
                      token,
                      propertyId,
                      property,
                      currentDomain: dashboardData?.currentDomain || '',
                      activeDataSources,
                      refetchData: fetchDashboardData,
                      showRevenue,
                      revenueLoading,
                      // Aggregate mode for workspace dashboards
                      isAggregateMode,
                      enabledProperties: isWorkspaceMode ? enabledProperties : [],
                      workspaceId: workspace?.workspaceId,
                    }}
                  />
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden min-w-0 bg-surface">
                  {/* Panel content inset (Paper): 28px top · 36px sides · 36px bottom */}
                  <div className="px-4 pt-5 pb-8 sm:px-9 sm:pt-7 sm:pb-9 max-w-[1600px] mx-auto">
                    <ShellContentProvider>
                    <AnalyticsTabsInShell>
                    {error && (
                      <div className="mb-4">
                        <InlineNotice
                          variant="error"
                          message={error}
                        />
                      </div>
                    )}

                    {!shouldBypassDashboardData && !pageOwnsLoading && isLoading && !dashboardData && (
                      <LoadingState
                        message="Loading dashboard data..."
                        size="lg"
                        fullPage
                      />
                    )}

                    {!shouldBypassDashboardData && !pageOwnsLoading && !isLoading && !dashboardData && !error && (
                      <LoadingState
                        message="Loading dashboard data..."
                        size="lg"
                        fullPage
                      />
                    )}

                    {/* Pass context to child routes via Outlet */}
                    <Outlet
                      context={{
                        dashboardData: dashboardData
                          ? {
                              ...dashboardData,
                              metrics: dashboardData.metrics || dashboardData.stats?.metrics || {},
                            }
                          : null,
                        filterOptionsData: filterOptionsData
                          ? {
                              ...filterOptionsData,
                              metrics: filterOptionsData.metrics || filterOptionsData.stats?.metrics || {},
                            }
                          : null,
                        isLoading: isLoading || (!shouldBypassDashboardData && !dashboardData),
                        period: state.period,
                        filters: state.filters,
                        sources: selectedSourceIds,
                        token,
                        propertyId,
                        property,
                        currentDomain: dashboardData?.currentDomain || '',
                        activeDataSources,
                        refetchData: fetchDashboardData,
                        showRevenue,
                        revenueLoading,
                        // Aggregate mode for workspace dashboards
                        isAggregateMode,
                        enabledProperties: isWorkspaceMode ? (isAggregateMode ? enabledProperties.filter(p => aggregatePropertyIds.includes(p.propertyId)) : enabledProperties) : [],
                        workspaceId: workspace?.workspaceId,
                      }}
                    />
                    </AnalyticsTabsInShell>
                    </ShellContentProvider>
                  </div>
                </div>
              )}
            </SidebarInset>
            </ShellHeaderProvider>
          </SidebarProvider>

          {/* Paywall Modals */}
          {/* Demo site: closeable paywall */}
          {isDemoProperty && propertyId && (
            <PaywallModal
              isOpen={showDemoPaywall}
              onClose={() => setShowDemoPaywall(false)}
              propertyId={propertyId}
            />
          )}

          {/* Scoped paywall controller — handles all three cases for /p/* and /workspace/* routes:
              1. Hard paywall (persistent, no close) for accounts with no trial/subscription
              2. Soft auto-nudge (closeable via openPricingModal()) for free users after 2s
              3. Manual open via openPricingModal() from upgrade CTAs / sidebar
              accessGranted exempts property members covered by the owner's paid sub.
              disabled prevents the flash while billing/site context is still loading,
              and opts demo properties and workspace aggregate mode out entirely. */}
          <AppPaywallController
            scoped
            accessGranted={propertyOwnerHasAccess}
            disabled={isDemoProperty || isWorkspaceMode || isDataLoading}
          />

          {/* Feature-specific upgrade modal (triggered by openUpgradeModal in SubscriptionContext) */}
          <UpgradeModal />
          </AIPageContextProvider>
        </AISidebarProvider>
      </DashboardWidgetProvider>
    </EditorModeProvider>
  );
}

